import { useNavigate, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { ErrorState, FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { ReportInterval, type SalesBucket } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Card, CardContent } from '@nucleus-commerce/ui/components/card';
import { Label } from '@nucleus-commerce/ui/components/label';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';
import { ToggleGroup, ToggleGroupItem } from '@nucleus-commerce/ui/components/toggle-group';

import { useStore } from '@/shared/store/store-context';

import { RangePicker } from '../components/range-picker';
import { ReportExportButton } from '../components/report-export-button';
import { RollupBar } from '../components/rollup-bar';
import { SalesChart } from '../components/sales-chart';
import { useDayFormat } from '../hooks/use-day-format';
import { useReportRange } from '../hooks/use-report-range';
import { useSalesReport } from '../hooks/use-sales-report';

const ROUTE_ID = '/_authed/_active/s/$storeId/reports/sales' as const;

function SalesReport() {
  const { t } = useTranslation('reports');
  const store = useStore();
  const format = useFormat();
  const days = useDayFormat();
  const { range, setRange } = useReportRange();
  const { interval = 'day' } = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: '/s/$storeId/reports/sales' });
  const query = useSalesReport(store.id, range, interval);
  const report = query.data;

  const period = (bucket: SalesBucket) =>
    interval === 'day' ? days.medium(bucket.start) : days.range(bucket.start, bucket.end);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1 lg:flex-row lg:items-start lg:justify-between">
        <RangePicker range={range} onChange={setRange} />
        <div className="flex flex-wrap items-center gap-3">
          <RollupBar storeId={store.id} range={range} lastRollupAt={report?.rollupAt} />
          <ReportExportButton storeId={store.id} report="sales" range={range} interval={interval} />
          <Label id="sales-interval-label" className="text-muted-foreground text-xs">
            {t('sales.interval')}
          </Label>
          <ToggleGroup
            data-tour="reports.sales.interval"
            type="single"
            variant="outline"
            size="sm"
            value={interval}
            aria-labelledby="sales-interval-label"
            onValueChange={(value) => {
              if (value === 'day' || value === 'week' || value === 'month') {
                void navigate({ search: (prev) => ({ ...prev, interval: value }) });
              }
            }}
          >
            {Object.values(ReportInterval).map((value) => (
              <ToggleGroupItem key={value} value={value}>
                {t(`sales.intervals.${value}`)}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      </div>
      {query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <>
          <Card data-tour="reports.sales.chart">
            <CardContent>
              {report ? (
                <SalesChart
                  currency={report.currency}
                  summary={t('overview.chartSummary', {
                    range: days.range(report.from, report.to),
                    gross: format.money(report.totals.gross),
                    orders: report.totals.ordersCount,
                  })}
                  points={report.buckets.map((bucket) => ({
                    label: days.short(bucket.start),
                    gross: bucket.gross,
                    ordersCount: bucket.ordersCount,
                  }))}
                />
              ) : (
                <Skeleton className="h-[280px] w-full" />
              )}
            </CardContent>
          </Card>
          <Card className="gap-0 overflow-hidden py-0" data-tour="reports.sales.table">
            <Table aria-label={t('sales.table')}>
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-5">{t('columns.period')}</TableHead>
                  <TableHead className="text-right">{t('columns.orders')}</TableHead>
                  <TableHead className="text-right">{t('columns.gross')}</TableHead>
                  <TableHead className="hidden text-right md:table-cell">
                    {t('columns.discounts')}
                  </TableHead>
                  <TableHead className="hidden text-right md:table-cell">
                    {t('columns.refunds')}
                  </TableHead>
                  <TableHead className="text-right">{t('columns.net')}</TableHead>
                  <TableHead className="hidden text-right lg:table-cell">
                    {t('columns.units')}
                  </TableHead>
                  <TableHead className="pr-5 text-right">{t('columns.aov')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {query.isPending
                  ? Array.from({ length: 6 }).map((_, i) => (
                      <TableRow key={i}>
                        <TableCell colSpan={8} className="px-5">
                          <Skeleton className="h-5" />
                        </TableCell>
                      </TableRow>
                    ))
                  : report?.buckets.map((bucket) => (
                      <TableRow key={bucket.start}>
                        <TableCell className="pl-5 whitespace-nowrap">{period(bucket)}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {format.number(bucket.ordersCount)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {format.money(bucket.gross)}
                        </TableCell>
                        <TableCell className="hidden text-right tabular-nums md:table-cell">
                          {format.money(bucket.discounts)}
                        </TableCell>
                        <TableCell className="hidden text-right tabular-nums md:table-cell">
                          {format.money(bucket.refunds)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {format.money(bucket.net)}
                        </TableCell>
                        <TableCell className="hidden text-right tabular-nums lg:table-cell">
                          {format.number(bucket.units)}
                        </TableCell>
                        <TableCell className="pr-5 text-right tabular-nums">
                          {format.money(bucket.aov)}
                        </TableCell>
                      </TableRow>
                    ))}
              </TableBody>
              {report ? (
                <TableFooter>
                  <TableRow>
                    <TableCell className="pl-5 font-medium">{t('sales.total')}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {format.number(report.totals.ordersCount)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {format.money(report.totals.gross)}
                    </TableCell>
                    <TableCell className="hidden text-right tabular-nums md:table-cell">
                      {format.money(report.totals.discounts)}
                    </TableCell>
                    <TableCell className="hidden text-right tabular-nums md:table-cell">
                      {format.money(report.totals.refunds)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {format.money(report.totals.net)}
                    </TableCell>
                    <TableCell className="hidden text-right tabular-nums lg:table-cell">
                      {format.number(report.totals.units)}
                    </TableCell>
                    <TableCell className="pr-5 text-right tabular-nums">
                      {format.money(report.totals.aov)}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              ) : null}
            </Table>
          </Card>
        </>
      )}
    </div>
  );
}

export function SalesReportPage() {
  return (
    <FeatureErrorBoundary name="reports.sales">
      <SalesReport />
    </FeatureErrorBoundary>
  );
}
